<script lang="ts" setup>
  import type { IOriginItem } from '@/components/clp/types'

  const props = defineProps<{
    data: IOriginItem[]
  }>()

  const modelValue = defineModel<IOriginItem | null>()

  const { isDesktop } = useBTBreakpoints()

  const autoCompleteSuggestion = ref<string | null>(null)

  const isOpen = ref<boolean>(false)
  function onItemSelect(item: IOriginItem) {
    if (!item?.city) {
      return
    }

    searchTerm.value = ''
    modelValue.value = item
    isOpen.value = false
  }

  function toggleDropdown() {
    isOpen.value = true
  }

  const searchTerm = ref<string>('')
  watchDebounced(
    searchTerm,
    () => {
      if (Number(searchTerm.value.length) < 2 || !filteredOrigins.value.length) {
        autoCompleteSuggestion.value = null

        return
      }

      const suggestion = `${filteredOrigins.value[0]?.city} (${filteredOrigins.value[0]?.code})`
      const input = searchTerm.value
      autoCompleteSuggestion.value = matchStringCase(suggestion, input)
    },
    { debounce: 100 }
  )

  const origKeys = ['apt', 'code', 'city', 'country'] as const
  const filteredOrigins = computed(() => {
    if (!searchTerm.value) {
      return props.data
    }

    return props.data.filter((origin) => {
      return origKeys.some((key) => {
        if (origin[key]) {
          return origin[key].toLowerCase().startsWith(searchTerm.value.toLowerCase())
        }

        return false
      })
    })
  })

  function highlightMatch(text: string, term: string) {
    if (!term) {
      return text
    }

    const escapedTerm = escapeSpecialCharacters(term)
    const regex = new RegExp(`(${escapedTerm})`, 'gi')

    return text.replace(regex, '<strong>$1</strong>')
  }

  function onModalOpenUpdate() {
    searchTerm.value = ''
  }

  const searchInput = ref<HTMLElement | null>(null)
  watch(searchInput, () => {
    if (searchInput.value) {
      nextTick(() => {
        searchInput.value?.focus()
      })
    }
  })
</script>
<template>
  <div class="relative">
    <button
      class="flex items-center gap-2 text-bt-blue-ui"
      @mouseup="toggleDropdown()"
      @keydown.enter.stop="toggleDropdown()"
    >
      <p class="mb-0 border-b border-bt-blue-ui text-h5">
        <template v-if="modelValue?.apt"> {{ modelValue.apt }}, ({{ modelValue.code }}) </template>
        <template v-else> {{ $t('forms.select.placeholder.default') }} </template>
      </p>
      <Icon
        name="common/chevron-right"
        class="rotate-90"
      />
    </button>
    <UiModalInput
      v-model="isOpen"
      :title="$t('origDest.title')"
      :show-close="!isDesktop"
      dialog-content-class="w-full border overflow-hidden md:w-90"
      @update:model-value="onModalOpenUpdate"
    >
      <template #content>
        <ListboxRoot
          :value="modelValue"
          @update:model-value="(val) => onItemSelect(val as IOriginItem)"
        >
          <div class="relative bg-white">
            <label
              v-if="searchTerm.length"
              class="text-bt-gray pointer-events-none absolute left-0 top-0 flex h-14 select-none items-center pl-2 text-gray-400"
            >
              {{ autoCompleteSuggestion }}
            </label>
            <input
              ref="searchInput"
              v-model="searchTerm"
              type="text"
              class="relative mb-0.5 h-14 w-full border-b bg-transparent px-2"
              :placeholder="$t('origDest.search.placeholder')"
              @keydown.enter.stop="onItemSelect(filteredOrigins[0])"
            />
          </div>
          <ListboxContent
            v-if="filteredOrigins.length"
            class="flex h-[calc(100svh-7rem)] w-full overflow-y-auto bg-white p-1 md:h-90"
          >
            <ListboxVirtualizer
              v-slot="{ option }"
              :estimate-size="68"
              :options="filteredOrigins"
              :text-content="(orig) => orig.apt"
            >
              <ListboxItem
                v-if="option.apt"
                :value="option"
                class="flex min-h-12 w-full cursor-pointer flex-col justify-between gap-1 rounded px-4 py-2 hover:bg-grey-select data-[highlighted]:bg-grey-select data-[state=checked]:bg-grey-select"
              >
                <span
                  class="font-medium"
                  v-html="
                    $t('origDest.aptCode', {
                      apt: highlightMatch(option.apt, searchTerm),
                      code: highlightMatch(option.code, searchTerm)
                    })
                  "
                />
                <span class="text-secondary text-body-3">
                  <span v-html="highlightMatch(option.city, searchTerm)" />,
                  <span v-html="highlightMatch(option.country, searchTerm)" />
                </span>
              </ListboxItem>
            </ListboxVirtualizer>
          </ListboxContent>
          <p
            v-else
            class="break-words px-2 py-4"
          >
            {{ $t('origDest.search.noResults') }} <b>{{ searchTerm }}</b>
          </p>
        </ListboxRoot>
      </template>
    </UiModalInput>
  </div>
</template>
